import type { LogsInitConfiguration } from '@datadog/browser-logs'
import type { RumInitConfiguration } from '@datadog/browser-rum'

import { DD_CLIENT_TOKEN } from './datadog-client-token'

// Client-side Datadog Browser RUM + Browser Logs for atlas-web, wired to React
// Router's client Instrumentation API (see ./datadog-instrumentation.client.ts).
// Trimmed port of apps/health/src/core-web/instrumentation.ts — no Seq (atlas-web
// has no Seq proxy) and no session replay. Client browser-logs land in the same
// Datadog Logs product as the server pino logs (`service: 'atlas-web'`), so the
// manual RUM → Logs debugging pivot spans both client and server.

const { VITE_ENVIRONMENT } = import.meta.env

/**
 * The deploy version (the production git SHA), read from the `app-version` meta tag that the
 * root route renders into the document.
 *
 * Deliberately NOT `import.meta.env.VITE_VERSION`. That is a concrete string rather than an
 * unresolved token, so it gets baked into whichever client chunks read it — and because the
 * SHA changes on every commit, every hashed bundle filename changed on every deploy, including
 * backend-only ones. Assets here are served `immutable` for a year, so every returning visitor
 * re-downloaded the lot for a change that never touched the frontend.
 *
 * There is no `index.html` to hold a `%VITE_VERSION%` placeholder — the document comes from
 * `root.tsx`, whose server-only `loader` React Router strips from the client bundle.
 *
 * Same reason, related trap: read `import.meta.env` with dot notation. Vite only replaces
 * `import.meta.env.KEY` statically; a computed `import.meta.env['KEY']` inlines the whole env
 * object, `VITE_VERSION` included, putting the SHA straight back into a hashed chunk.
 */
const getAppVersion = (): string => {
  if (typeof document === 'undefined') {
    return 'N/A'
  }

  const version = document.querySelector('meta[name="app-version"]')?.getAttribute('content')

  return version == null || version === '' ? 'N/A' : version
}

// Public, org-scoped Datadog browser client token, authorising both the RUM and Logs intake.
// `service: 'atlas-web'` separates this telemetry from the other apps within the same DD org.
// Held in ./datadog-client-token.ts because app/content-security-policy.ts builds the CSP
// `report-uri` intake URL from the same token and cannot import this module.

// Browser RUM application id, minted with the dedicated `atlas-web` Browser RUM
// Application in Datadog (US1). datadogRum.init() is a no-op without a valid id,
// and RUM only inits in production (below).
const DD_RUM_APPLICATION_ID = 'af19729f-521b-4719-92f1-5693a23725bb'

/**
 * Returns a timestamp string in a "HH:MM:SS" format (for the non-production
 * console log formatter below).
 */
function getTimestamp(): string {
  const now = new Date()

  return [now.getHours(), now.getMinutes(), now.getSeconds()]
    .map(String)
    .map((chunk) => chunk.slice(0, 2))
    .map((chunk) => chunk.padStart(2, '0'))
    .join(':')
}

type StatusCode = 'error' | 'alert' | 'debug' | 'info' | 'warn' | 'ok' | 'notice' | 'critical' | 'emerg'

const StatusCodeColor = {
  Debug: 'inherit', // Use the default color for Debug
  Error: '#E95F5D',
  Info: '#89cff0',
  Warning: '#F0BB4B',
} as const

type StatusCodeColor = (typeof StatusCodeColor)[keyof typeof StatusCodeColor]

/**
 * Returns a HEX color for a given Datadog log status.
 */
function getStatusCodeColor(status: StatusCode): StatusCodeColor {
  if (status === 'debug') {
    return StatusCodeColor.Debug
  }

  if (['error', 'alert', 'critical', 'emerg'].includes(status)) {
    return StatusCodeColor.Error
  }

  if (['info', 'ok', 'notice'].includes(status)) {
    return StatusCodeColor.Info
  }

  return StatusCodeColor.Warning
}

// Only allow keys in sharedDatadogConfig that are common across Logs and RUM.
type SharedProperties<T, U> = Partial<Pick<T & U, keyof T & keyof U>>

const sharedDatadogConfig = {
  clientToken: DD_CLIENT_TOKEN,
  env: VITE_ENVIRONMENT,
  service: 'atlas-web',
  sessionPersistence: 'local-storage',
  sessionSampleRate: 100,
  site: 'datadoghq.com',
  storeContextsAcrossPages: true,
  version: getAppVersion(),
} satisfies SharedProperties<RumInitConfiguration, LogsInitConfiguration>

// Holds the initialized RUM API so the RR client instrumentation can be a no-op
// until/unless RUM is live (i.e. outside production). See getRum().
type DatadogRum = typeof import('@datadog/browser-rum').datadogRum
let rum: DatadogRum | undefined

/**
 * Returns the initialized Datadog RUM API, or `undefined` when RUM was not
 * initialized (non-production). Lets ./datadog-instrumentation.client.ts stay a
 * no-op without knowing whether RUM is live.
 */
export const getRum = (): DatadogRum | undefined => rum

export const initInstrumentation = async () => {
  if (VITE_ENVIRONMENT === 'production') {
    const { datadogRum } = await import('@datadog/browser-rum')

    datadogRum.init({
      ...sharedDatadogConfig,
      applicationId: DD_RUM_APPLICATION_ID,
      compressIntakeRequests: true,
      defaultPrivacyLevel: 'mask-user-input',
      sessionReplaySampleRate: 100,
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      // Self-host the compression worker from our own bundle (avoids a
      // worker-src CSP allowance for the Datadog CDN). Mirrors apps/health.
      workerUrl: new URL('@datadog/browser-worker', import.meta.url).href,
      beforeSend(event) {
        if (event.type === 'error') {
          // Filter out unactionable CSP / browser-extension errors.
          if (
            event.error?.stack?.includes('chrome-extension:') ||
            event.error?.stack?.includes('safari-extension:') ||
            event.error?.stack?.includes('safari-web-extension:') ||
            event.error?.stack?.includes('user-script:') ||
            event.error?.stack?.includes('sendNativeMessage()') ||
            event.error?.message?.includes("blocked by 'font-src'")
          ) {
            return false
          }

          // Navigation-aborted fetch requests (browser kills in-flight fetches on
          // page transitions). Chrome/Edge: "Failed to fetch", Safari: "Load
          // failed" / "cancelled", Firefox: "NetworkError...".
          if (
            event.error?.message?.includes('Failed to fetch') ||
            event.error?.message?.includes('Load failed') ||
            event.error?.message?.includes('cancelled') ||
            event.error?.message?.includes('Fetch is aborted') ||
            event.error?.message?.includes('The user aborted a request') ||
            event.error?.message?.includes('NetworkError when attempting to fetch resource')
          ) {
            return false
          }

          // View Transitions API: browser-side, unactionable. Errors thrown inside
          // our update callback surface with their original message (not wrapped
          // with "view transition" text), so this does not hide real bugs.
          const msg = event.error?.message?.toLowerCase() ?? ''
          if (
            msg.includes('view transition') ||
            msg.includes('viewtransition') ||
            msg.includes('transition was skipped')
          ) {
            return false
          }

          // InvalidStateError: unactionable DOMException from browser internals
          // (e.g. accessing a destroyed DOM node, closed XHR, aborted view transition).
          if (event.error?.type === 'InvalidStateError') {
            return false
          }
        }

        return true
      },
    })

    rum = datadogRum
  }

  const { datadogLogs } = await import('@datadog/browser-logs')

  datadogLogs.init({
    ...sharedDatadogConfig,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error'],
    beforeSend: (log) => {
      // Navigation-aborted or browser-cancelled fetch requests (status_code 0
      // means the browser killed the request).
      if (log.http?.status_code === 0) {
        return false
      }

      // Network errors from browser-cancelled fetches during navigation.
      if (
        [
          'Fetch error POST',
          'XHR error POST',
          'Fetch error GET',
          'XHR error GET',
          'Failed to fetch',
          'Load failed',
          'cancelled',
          'NetworkError when attempting to fetch resource.',
          'The request timed out.',
          'Fetch is aborted',
          'The user aborted a request',
        ].some(
          (ignored) =>
            log.message.includes(ignored) ||
            log.error?.message?.includes(ignored) ||
            (typeof log['errorDescription'] === 'string' && log['errorDescription'].includes(ignored)),
        )
      ) {
        return false
      }

      // Browser-extension / third-party noise.
      if (
        log.error?.stack?.includes('chrome-extension:') ||
        log.error?.stack?.includes('sendNativeMessage()') ||
        log.message.includes('Invalid call to runtime.sendMessage(). Tab not found.')
      ) {
        return false
      }

      // View Transitions API: browser-side, unactionable (see RUM note above).
      const logMsg = log.message?.toLowerCase() ?? ''
      const errMsg = log.error?.message?.toLowerCase() ?? ''
      if (
        logMsg.includes('view transition') ||
        logMsg.includes('viewtransition') ||
        logMsg.includes('transition was skipped') ||
        errMsg.includes('view transition') ||
        errMsg.includes('viewtransition') ||
        errMsg.includes('transition was skipped')
      ) {
        return false
      }

      // InvalidStateError: unactionable DOMException from browser internals.
      if (log.error?.kind === 'InvalidStateError' || log.message.includes('InvalidStateError')) {
        return false
      }

      // React Router re-logs the original error with this prefix — always a
      // duplicate of the preceding error log.
      if (log.message.includes('React Router caught the following error during render')) {
        return false
      }

      // In non-production environments, log to the console instead of Datadog.
      if (VITE_ENVIRONMENT !== 'production') {
        console.groupCollapsed(
          `[DATADOG] ${getTimestamp()} \`${log.message}\` (%c${String(log.status).toUpperCase()}%c)`,
          `color:${getStatusCodeColor(log.status)};`,
          'color:inherit;',
        )
        console.log('Payload', log)
        console.groupEnd()

        return false
      }

      return true
    },
  })

  datadogLogs.onReady(() => {
    if (VITE_ENVIRONMENT !== 'production') {
      console.groupCollapsed(
        '%c[DATADOG] Logs being intercepted for non-production environments',
        'color:yellow;font-weight:bold;',
      )
      console.log('Change in the `app/instrumentation.client.ts` file if needed')
      console.groupEnd()
    }
  })
}
